/**
 * 模組頁面代碼 : 1340-UI-03
 * 頁面說明 : 密碼修改視窗
 * @module web/modules/role-permission/components/UserPasswordModal
 */

// ----- import from npm modules -----
import React, { useState, useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';

// ----- import from local modules -----
import PasswordInput from '../../../components/PasswordInput';

// ----- contents -----
const RESPONSE_OK = 200;
const TITLE_USER_PASSWORD = '修改密碼';

const FIXED_SALT = process.env.REACT_APP_FIXED_SALT;

const UserPasswordModal = React.forwardRef((props, ref) => {
    const { user, userId, account } = props;

    // 初始化state
    const [formValues, setFormValues] = useState({
        modalPasswordAccount: '',
        modalPasswordPswOld: '',
        modalPasswordPsw: '',
        modalPasswordPswConfirm: '',
    });

    const [invalidShow, setInvalidShow] = useState('none');

    // reset PasswordInput
    const [resetPasswordInput, setResetPasswordInput] = useState(false);
    const [resetconfirmPasswordInput, setResetConfirmPasswordInput] = useState(false);
    const [resetOldPasswordInput, setResetOldPasswordInput] = useState(false);

    // Handle change event
    const handleChange = (event) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        });
    };

    // 使用useEffect在account改變才更新formValues
    useEffect(() => {
        setFormValues({
            modalPasswordAccount: user?.account || '',
            modalPasswordPswOld: '',
            modalPasswordPsw: '',
            modalPasswordPswConfirm: '',
        });
        setInvalidShow('none');
    }, [user?.account]);

    // 使用useEffect在formValues.modalPasswordPsw改變時更新消息
    useEffect(() => {
        if (formValues.modalPasswordPswOld !== '' && formValues.modalPasswordPswOld !== formValues.modalPasswordPsw) {
            setResetOldPasswordInput(true);
        }
        if (formValues.modalPasswordPswConfirm !== '' && formValues.modalPasswordPsw === formValues.modalPasswordPswConfirm) {
            setResetConfirmPasswordInput(true);
        }
    }, [formValues.modalPasswordPsw]);

    // 使用useEffect在formValues.modalPasswordPswConfirm改變時更新消息
    useEffect(() => {
        if (formValues.modalPasswordPsw !== '' && formValues.modalPasswordPsw === formValues.modalPasswordPswConfirm) {
            setResetPasswordInput(true);
        }
    }, [formValues.modalPasswordPswConfirm]);

    // 使用useEffect在formValues.modalPasswordPswOld改變時更新消息
    useEffect(() => {
        if (formValues.modalPasswordPsw !== '' && formValues.modalPasswordPswOld !== formValues.modalPasswordPsw) {
            setResetPasswordInput(true);
        }
    }, [formValues.modalPasswordPswOld]);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        // 檢查表单是否验证通过
        if (form.checkValidity() === false) {
            setInvalidShow('block');
            form.classList.add('was-validated');
            return;
        }

        // 建立 FormData 讀取表單的值
        const formData = new FormData(form);

        // 將密碼加密
        const oldPassword = formData.get('modalPasswordPswOld');
        const newPassword = formData.get('modalPasswordPsw');
        let encryptedOldPsw = '';
        let encryptedNewPsw = '';
        if (oldPassword) {
            encryptedOldPsw = CryptoJS.SHA256(oldPassword + FIXED_SALT).toString();
            formData.set('modalPasswordPswOld', encryptedOldPsw);
        }
        if (newPassword) {
            encryptedNewPsw = CryptoJS.SHA256(newPassword + FIXED_SALT).toString();
            formData.set('modalPasswordPsw', encryptedNewPsw);
        }

        // 調用props.editUserPassword
        props.editUserPassword(user.userId, encryptedOldPsw, encryptedNewPsw).then((response) => {
            const statusCode = response.payload.code;
            if (statusCode !== RESPONSE_OK) {
                $(ref.current).modal('hide');
                props.onErrorHandle(TITLE_USER_PASSWORD, response.payload);
            } else {
                // 關閉視窗
                $(ref.current).modal('hide');
            }
        });
    };

    return (
        <div ref={ref}
            className="modal fade" id="modalPassword" tabIndex="-1" aria-labelledby="modalPasswordLabel" aria-hidden="true">
            <div className="modal-dialog modal-sm modal-dialog-centered">
                <form className="modal__form modal-content needs-validation" id="modalPasswordForm" onSubmit={handleSubmit}>
                    <div className="modal-header border-bottom-0">
                        <h1 className="modal-title fs-5" id="modalPasswordLabel">
                            <div className="modal__title__badge" id="modalPasswordBadge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href="#ico-lock"></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">修改密碼</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            <div className="row">
                                <div className="col-12">
                                    <label htmlFor="modalPasswordAccount" className="form-label">帳號</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        id="modalPasswordAccount"
                                        name="modalPasswordAccount"
                                        value={formValues.modalPasswordAccount}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <label htmlFor="modalPasswordPswOld" className="form-label">舊密碼*</label>
                                    <PasswordInput
                                        id="modalPasswordPswOld"
                                        name="modalPasswordPswOld"
                                        value={formValues.modalPasswordPswOld}
                                        confirmValue={formValues.modalPasswordPsw}
                                        onChange={handleChange}
                                        validateType="oldPassword"
                                        placeholder=""
                                        resetMessage={resetOldPasswordInput}
                                        setResetMessage={(bReset) => setResetOldPasswordInput(bReset)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <label htmlFor="modalPasswordPsw" className="form-label">新密碼*</label>
                                    <PasswordInput
                                        id="modalPasswordPsw"
                                        name="modalPasswordPsw"
                                        value={formValues.modalPasswordPsw}
                                        oldValue={formValues.modalPasswordPswOld}
                                        confirmValue={formValues.modalPasswordPswConfirm}
                                        onChange={handleChange}
                                        validateType="newPassword"
                                        placeholder=""
                                        defaultMassage="密碼長度為8-16碼，至少包含1個以上大寫英文、小寫英文、數字、符號"
                                        resetMessage={resetPasswordInput}
                                        setResetMessage={(bReset) => setResetPasswordInput(bReset)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <label htmlFor="modalPasswordPswConfirm" className="form-label">確認新密碼*</label>
                                    <PasswordInput
                                        id="modalPasswordPswConfirm"
                                        name="modalPasswordPswConfirm"
                                        value={formValues.modalPasswordPswConfirm}
                                        confirmValue={formValues.modalPasswordPsw}
                                        onChange={handleChange}
                                        validateType="confirmPassword"
                                        placeholder=""
                                        defaultMassage="請確認與輸入密碼相符"
                                        resetMessage={resetconfirmPasswordInput}
                                        setResetMessage={(bReset) => setResetConfirmPasswordInput(bReset)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="alert alert-warning global-invalid-feedback" style={{ display: invalidShow }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default UserPasswordModal;